import React, { useState, useEffect } from "react";
import {
    Nav, NavItem, NavLink, TabContent, TabPane,
    FormGroup, Label, Input, Alert
} from "reactstrap";
import { BUSINESS_RULES_CATEGORIES, BUSINESS_RULES_DEFINITIONS } from "../../../../models/businessRule";
import { LoadingButton } from "../../../../components/Common";

/**
 * BusinessRulesDetail
 * Painel direito do Master-Detail.
 * Exibe as regras de negócio da categoria selecionada com formulário de edição.
 */
const BusinessRulesDetail = ({ categoryId, rules, onSave, isSaving }) => {

    const catInfo = BUSINESS_RULES_CATEGORIES.find(c => c.id === categoryId) || { icon: "mdi-cog", description: "" };
    const categoryRules = BUSINESS_RULES_DEFINITIONS.filter(r => r.category === categoryId);

    const [activeRuleId, setActiveRuleId] = useState("");
    const [formData, setFormData] = useState({});
    const [isDirty, setIsDirty] = useState(false);

    // Selecionar primeira regra ao mudar de categoria
    useEffect(() => {
        if (categoryRules.length > 0) {
            const firstId = categoryRules[0].id;
            setActiveRuleId(firstId);
            setFormData({ ...rules });
            setIsDirty(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    // Sync com rules externas
    useEffect(() => {
        setFormData({ ...rules });
    }, [rules]);

    const handleFieldChange = (ruleId, value) => {
        setFormData(prev => ({ ...prev, [ruleId]: value }));
        setIsDirty(true);
    };

    const handleSubmit = () => {
        onSave(formData);
        setIsDirty(false);
    };

    const activeRule = categoryRules.find(r => r.id === activeRuleId);

    if (!categoryId) {
        return (
            <div className="border border-top rounded-0 h-100 overflow-hidden bg-white">
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                    <div className="text-center text-muted">
                        <i className="mdi mdi-arrow-left-bold font-size-48 opacity-25 mb-3 d-block"></i>
                        <p className="font-size-14 mb-0">Selecione uma categoria para ver as regras.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="border border-top rounded-0 h-100 overflow-hidden bg-white">
            {/* Header da categoria */}
            <div className="bg-white border-bottom py-3 px-4">
                <div className="d-flex align-items-center">
                    <i className={`mdi ${catInfo.icon} font-size-24 me-3 text-primary`}></i>
                    <div>
                        <h6 className="mb-0 fw-bold text-dark">{categoryId}</h6>
                        <p className="text-muted small mb-0">{catInfo.description}</p>
                    </div>
                </div>
            </div>

            {/* Tabs de regras (só exibe se houver mais de 1) */}
            {categoryRules.length > 1 && (
                <Nav tabs className="px-3 pt-3 border-bottom-0 rounded-0">
                    {categoryRules.map(rule => (
                        <NavItem key={rule.id}>
                            <NavLink
                                className={`cursor-pointer d-flex align-items-center gap-1 rounded-0 ${activeRuleId === rule.id ? 'active fw-bold' : 'text-muted'}`}
                                onClick={() => setActiveRuleId(rule.id)}
                                style={{ fontSize: '13px', cursor: 'pointer' }}
                            >
                                <i className={`mdi ${rule.icon} font-size-14`}></i>
                                <span className="text-truncate" style={{ maxWidth: '160px' }}>{rule.label}</span>
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
            )}

            {/* Conteúdo */}
            <TabContent activeTab={activeRuleId} className="p-4">
                {activeRule && (
                    <TabPane tabId={activeRuleId}>
                        {/* Campo de configuração */}
                        <div className="mb-4">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold font-size-13 d-block">{activeRule.label}</Label>
                                <div className="d-flex align-items-center gap-2" style={{ maxWidth: activeRule.inputType === 'boolean' ? '100%' : '260px' }}>
                                    {activeRule.inputType === 'boolean' ? (
                                        <div className="form-check form-switch form-switch-lg" dir="ltr">
                                            <Input
                                                type="switch"
                                                id={`switch-${activeRule.id}`}
                                                className="form-check-input"
                                                checked={formData[activeRule.id] ?? activeRule.defaultValue}
                                                onChange={e => handleFieldChange(activeRule.id, e.target.checked)}
                                            />
                                            <Label className="form-check-label ms-2 mt-1" htmlFor={`switch-${activeRule.id}`}>
                                                {formData[activeRule.id] ? "Ativado" : "Desativado"}
                                            </Label>
                                        </div>
                                    ) : activeRule.inputType === 'text' || activeRule.inputType === 'string' ? (
                                            <Input
                                                type="text"
                                                value={formData[activeRule.id] ?? activeRule.defaultValue}
                                                onChange={e => handleFieldChange(activeRule.id, e.target.value)}
                                                className="rounded-0 py-2 border"
                                            />
                                        ) : (
                                            <Input
                                                type="number"
                                                min={activeRule.min || 0}
                                                max={activeRule.max || 999}
                                                value={formData[activeRule.id] ?? activeRule.defaultValue}
                                                onChange={e => handleFieldChange(activeRule.id, Number(e.target.value))}
                                                className="rounded-0 py-2 border"
                                            />
                                        )}
                                    
                                    {activeRule.suffix && activeRule.inputType !== 'boolean' && (
                                        <span className="text-muted font-size-13 fw-semibold">{activeRule.suffix}</span>
                                    )}
                                </div>
                                {activeRule.helpText && (
                                    <small className="text-muted d-block mt-2">
                                        <i className="mdi mdi-information-outline me-1"></i>
                                        {activeRule.helpText}
                                    </small>
                                )}
                            </FormGroup>
                        </div>

                        {/* Alerta de valor 0 - APENAS para números e para regra de dias de carência */}
                        {activeRule.inputType !== 'boolean' && formData[activeRule.id] === 0 && activeRule.id === 'membershipGraceDays' && (
                            <Alert color="warning" className="py-2 px-3 font-size-12 mb-3">
                                <i className="mdi mdi-alert-outline me-1"></i>
                                Com <strong>0 dias</strong>, o aluno será inativado imediatamente na data de vencimento do contrato.
                            </Alert>
                        )}

                        {/* Botão salvar */}
                        <div className="d-flex justify-content-end pt-3 border-top">
                            <LoadingButton
                                color="primary"
                                className="rounded-0 px-4"
                                loading={isSaving}
                                onClick={handleSubmit}
                                disabled={!isDirty}
                            >
                                Salvar
                            </LoadingButton>
                        </div>
                    </TabPane>
                )}
            </TabContent>
        </div>
    );
};

export default BusinessRulesDetail;
